import "zod/compile";
import { z } from "zod";

/**
 * What a Nami action tool returns to the model — and what the chat renders as
 * a change card or a preview card. One schema on both sides so the card can
 * never drift from the tool.
 */

export const HarnessActionEntitySchema = z.object({
  kind: z.string(),
  id: z.string(),
  label: z.string(),
  /** App path to open the entity, when the page exists. */
  path: z.string().optional(),
});
export type HarnessActionEntity = z.infer<typeof HarnessActionEntitySchema>;

export const HarnessActionChangeSchema = z.object({
  label: z.string(),
  before: z.unknown().optional(),
  after: z.unknown().optional(),
});
export type HarnessActionChange = z.infer<typeof HarnessActionChangeSchema>;

export const HarnessActionPreviewSchema = z.object({
  title: z.string(),
  summary: z.string(),
  warnings: z.array(z.string()).optional(),
  table: z
    .object({
      columns: z.array(z.string()),
      rows: z.array(z.array(z.union([z.string(), z.number(), z.null()]))),
    })
    .optional(),
  facts: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
});
export type HarnessActionPreview = z.infer<typeof HarnessActionPreviewSchema>;

export const HarnessActionAppliedResultSchema = z.object({
  status: z.literal("applied"),
  toolName: z.string(),
  toolCallId: z.string(),
  summary: z.string(),
  changes: z.array(HarnessActionChangeSchema).optional(),
  entity: HarnessActionEntitySchema.optional(),
  /** Dotted contract paths whose queries the page should refetch. */
  invalidates: z.array(z.string()),
  /** ISO timestamp until which `harness.actions.undo` accepts this call. */
  undoAvailableUntil: z.string().optional(),
});

export const HarnessActionPendingResultSchema = z.object({
  status: z.literal("pending_user_approval"),
  toolName: z.string(),
  toolCallId: z.string(),
  pendingActionId: z.string(),
  expiresAt: z.string(),
  preview: HarnessActionPreviewSchema,
  entity: HarnessActionEntitySchema.optional(),
  invalidates: z.array(z.string()),
});

export const HarnessActionFailedResultSchema = z.object({
  status: z.literal("failed"),
  toolName: z.string(),
  code: z.string(),
  message: z.string(),
});

export const HarnessActionToolResultSchema = z.discriminatedUnion("status", [
  HarnessActionAppliedResultSchema,
  HarnessActionPendingResultSchema,
  HarnessActionFailedResultSchema,
]);
export type HarnessActionToolResult = z.infer<
  typeof HarnessActionToolResultSchema
>;
export type HarnessActionAppliedResult = z.infer<
  typeof HarnessActionAppliedResultSchema
>;
export type HarnessActionPendingResult = z.infer<
  typeof HarnessActionPendingResultSchema
>;
export type HarnessActionFailedResult = z.infer<
  typeof HarnessActionFailedResultSchema
>;

/** Reads a tool result as a harness action result, or null for any other tool. */
export function parseHarnessActionResult(
  value: unknown,
): HarnessActionToolResult | null {
  const parsed = HarnessActionToolResultSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
